import { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, Tag } from "lucide-react";
import Navigation from "@/components/layout/Navigation";
import Footer from "@/components/layout/Footer";
import LazyVideoEmbed from "@/components/media/LazyVideoEmbed";
import AudioEmbed from "@/components/media/AudioEmbed";
import { supabase } from "@/integrations/supabase/client";

interface Post {
  id: string;
  title: string;
  slug: string | null;
  excerpt: string | null;
  content: string | null;
  image_url: string | null;
  video_url: string | null;
  audio_url: string | null;
  category: string | null;
  published_at: string | null;
  created_at: string;
}

const FieldNotePost = () => {
  const { id } = useParams<{ id: string }>();
  const [post, setPost] = useState<Post | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (id) fetchPost(id);
  }, [id]);

  const fetchPost = async (identifier: string) => {
    try {
      let { data, error } = await supabase
        .from("posts")
        .select("*")
        .eq("slug", identifier)
        .not("published_at", "is", null)
        .maybeSingle();

      if (!data) {
        ({ data, error } = await supabase
          .from("posts")
          .select("*")
          .eq("id", identifier)
          .not("published_at", "is", null)
          .maybeSingle());
      }

      if (error) throw error;
      if (!data) {
        setNotFound(true);
      } else {
        setPost(data);
      }
    } catch (error) {
      console.error("Error fetching post:", error);
      setNotFound(true);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="pt-24 pb-16 paper-texture">
          <div className="container mx-auto px-4 text-center">
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="pt-24 pb-16 paper-texture">
          <div className="container mx-auto px-4 text-center">
            <h1 className="font-display text-3xl font-bold text-primary mb-4">Post Not Found</h1>
            <p className="text-muted-foreground mb-6">This field note doesn't exist or hasn't been published yet.</p>
            <Link to="/field-notes" className="inline-flex items-center gap-2 text-accent hover:text-accent/80 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back to Field Notes
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="pt-24 pb-16 paper-texture">
        <article className="container mx-auto px-4">
          {/* Back Link */}
          <div className="max-w-3xl mx-auto mb-8">
            <Link to="/field-notes" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors font-mono text-sm uppercase tracking-wider">
              <ArrowLeft className="w-4 h-4" /> Back to Field Notes
            </Link>
          </div>

          {/* Title */}
          <header className="max-w-[800px] mx-auto text-center mb-6">
            {post.category && (
              <span className="inline-flex items-center gap-1 bg-secondary/20 text-secondary px-3 py-1 rounded-full text-sm font-mono uppercase tracking-wider mb-4">
                <Tag className="w-3 h-3" /> {post.category}
              </span>
            )}
            <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-3">{post.title}</h1>
            {post.published_at && (
              <p className="flex items-center justify-center gap-2 text-muted-foreground">
                <Calendar className="w-4 h-4" /> {formatDate(post.published_at)}
              </p>
            )}
          </header>

          {/* Hero Media — capped height, narrow container */}
          {post.video_url && (
            <div className="max-w-[800px] mx-auto mb-10 max-h-[40vh] md:max-h-[500px] overflow-hidden rounded-lg">
              <LazyVideoEmbed url={post.video_url} title={post.title} />
            </div>
          )}

          {post.audio_url && (
            <div className="max-w-[800px] mx-auto mb-10">
              <AudioEmbed url={post.audio_url} />
            </div>
          )}

          {!post.video_url && !post.audio_url && post.image_url && (
            <div className="max-w-[800px] mx-auto mb-10">
              <img
                src={post.image_url}
                alt={post.title}
                className="w-full max-h-[40vh] md:max-h-[500px] object-cover rounded-lg"
              />
            </div>
          )}

          {/* Content */}
          <div className="max-w-[800px] mx-auto">
            <div className="prose prose-lg max-w-none prose-p:mb-4 prose-ul:my-4 prose-ul:list-disc prose-ul:pl-6 prose-li:mb-1 prose-strong:font-bold scrapbook-card p-8 md:p-12" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content || "") }} />
          </div>

          {/* Back Link Bottom */}
          <div className="max-w-3xl mx-auto mt-12 text-center">
            <Link to="/field-notes" className="inline-flex items-center gap-2 text-accent hover:text-accent/80 transition-colors font-mono uppercase tracking-wider">
              <ArrowLeft className="w-4 h-4" /> More Field Notes
            </Link>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default FieldNotePost;